import { AnimatePresence, LayoutGroup } from 'framer-motion';
import moment from 'moment';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useFillViewportHeight, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Callout } from '@treams/treams-ui/common/Callout';
import { Container } from '@treams/treams-ui/common/Container';
import {
	ArrowLeftIcon,
	CheckmarkIcon,
	CircleCheckmarkIcon,
	PencilIcon,
	UndoIcon,
} from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

import CancelTalentReviewCorrectionModal from './CancelTalentReviewCorrectionModal';
import ClearTalentReviewGridModal from './ClearTalentReviewGridModal';
import TalentReviewActivity from './components/TalentReviewActivity';
import TalentReviewCurrentSelect from './components/TalentReviewCurrentSelect';
import TalentReviewDateNavigation from './components/TalentReviewDateNavigation';
import TalentReviewGridActionsMenu from './components/TalentReviewGridActionsMenu';
import TalentReviewPageHeader from './components/TalentReviewPageHeader';
import TalentReviewPlacementHelpModal from './components/TalentReviewPlacementHelpModal';
import TalentReviewSectionTitle from './components/TalentReviewSectionTitle';
import TalentReviewSidebar from './components/TalentReviewSidebar';
import TalentReviewSplitLayout from './components/TalentReviewSplitLayout';
import TalentReviewUnplaced from './components/TalentReviewUnplaced';
import useTalentReview from './hooks/useTalentReview';
import useTalentReviewCorrectionChanges from './hooks/useTalentReviewCorrectionChanges';
import useTalentReviewPath, {
	talentReviewSearchWithUserID,
	talentReviewUserIDFromSearch,
} from './hooks/useTalentReviewPath';
import RevertTalentReviewModal from './RevertTalentReviewModal';
import SaveTalentReviewCorrectionModal from './SaveTalentReviewCorrectionModal';
import SubmitTalentReviewModal from './SubmitTalentReviewModal';
import { type TalentReviewSnapshotContext } from './TalentReview';
import TalentReviewGridContainer from './TalentReviewGridContainer';
import { cellPlacements, insertPosition, placementPosition } from './talentReviewCellOrder';
import { cellColorValue } from './talentReviewColors';
import { formatTalentReviewDeadline, isTalentReviewHistoryDay } from './talentReviewDate';
import {
	TalentReviewInsertionContext,
	useTalentReviewInsertionTracking,
} from './talentReviewInsertion';
import { TalentReviewMemberOrderContext } from './talentReviewMemberOrder';
import {
	type TemplatePlacementHelpQuestion,
	asPlacementHelpAxis,
	placementHelpPositionLabel,
	questionsOnTemplate,
	withPlacementHelpName,
} from './talentReviewPlacementHelp';
import { type TalentReviewPresence, TalentReviewPresenceContext } from './talentReviewPresence';

export default function TalentReviewV2(props: {
	grid: graphql.TalentReviewGrid;
	templateID: string;
	groupID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
	snapshot?: TalentReviewSnapshotContext;
	header?: ReactNode;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();
	const authenticatedUser = useAuthenticatedUser();
	// The submitted callout otherwise waits for the moment refetch, so the
	// yellow one stays visible behind the dialog until that returns. Cleared
	// once that refetch confirms the submit, so a later reopen is not stuck green.
	const [optimisticMomentID, setOptimisticMomentID] = useState<string>();
	const path = useTalentReviewPath();
	// Submitting and reverting add an activity row to the live grid, so that
	// query is refetched too. It is not active on a snapshot page (the grid
	// comes in through props), and naming an inactive query only logs a warning.
	const submissionRefetchQueries = [
		'TalentReviewSnapshots',
		'MomentForTalentReview',
		...(props.snapshot ? [] : ['TalentReviewGrid', 'TalentReviewCycleMoment']),
	];
	const [createSnapshot, { loading: submitting }] = graphql.useCreateTalentReviewSnapshotMutation({
		refetchQueries: submissionRefetchQueries,
	});
	const [revertSubmission, { loading: reverting }] =
		graphql.useRevertTalentReviewSubmissionMutation({
			refetchQueries: submissionRefetchQueries,
		});
	// No refetch: the response carries the snapshot with its id, so the cache
	// updates the open snapshot query by itself.
	const [correctSnapshot, { loading: savingCorrection }] =
		graphql.useCorrectTalentReviewSnapshotMutation();
	const history = useHistory();
	const location = useLocation();

	// On a past day the activity list stops at that day, matching the grid as
	// it was back then. Snapshots already carry the activity up to submission.
	const activity = useMemo(
		() =>
			props.snapshot
				? props.grid.activity
				: props.grid.activity.filter(
						(item) => moment(item.createdAt).format('YYYY-MM-DD') <= props.localDate,
					),
		[props.grid.activity, props.localDate, props.snapshot],
	);

	// Superuser correction of a submitted snapshot: the grid becomes editable,
	// changes stay local until saved in one go.
	const [correcting, setCorrecting] = useState(false);
	const [showSaveCorrectionModal, setShowSaveCorrectionModal] = useState(false);
	const [showCancelCorrectionModal, setShowCancelCorrectionModal] = useState(false);
	// Where the user wanted to go when navigation was blocked mid-correction.
	const pendingLocation = useRef<Parameters<typeof history.push>[0]>();

	// The content and the sidebar each scroll on their own; the split fills the
	// room left in the viewport.
	const splitRef = useRef<HTMLDivElement>(null);
	useFillViewportHeight(splitRef);

	const {
		today,
		placements,
		stale,
		readonly,
		users,
		unplacedUsers,
		cycleMoment,
		submitted,
		daysLeft,
		movePlacement,
		removePlacement,
		clearGrid,
		updatePlacement,
		updatePlacements,
		updateMotivation,
		flushMotivation,
		answerPlacementHelp,
		resetPlacements,
	} = useTalentReview({
		grid: props.grid,
		templateID: props.templateID,
		groupID: props.groupID,
		localDate: props.localDate,
		isSnapshot: !!props.snapshot,
		correcting,
	});

	const correctionChanges = useTalentReviewCorrectionChanges(
		correcting,
		props.grid.placements,
		placements,
	);
	const correctionDirty = correcting && correctionChanges > 0;

	// Block in-app navigation (links, back button) while there are unsaved
	// corrections and show the cancel modal instead; the browser's own dialog
	// covers reloads and closing the tab.
	useEffect(() => {
		if (!correctionDirty) {
			return;
		}

		const unblock = history.block((location) => {
			pendingLocation.current = location;
			setShowCancelCorrectionModal(true);
			return false;
		});
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);

		return () => {
			unblock();
			window.removeEventListener('beforeunload', onBeforeUnload);
		};
	}, [correctionDirty, history]);

	const stopCorrecting = () => {
		resetPlacements();
		setMovedUserIDs([]);
		setCorrecting(false);
		setShowCancelCorrectionModal(false);
		setShowSaveCorrectionModal(false);
	};

	const cancelCorrection = () => {
		if (correctionDirty) {
			pendingLocation.current = undefined;
			setShowCancelCorrectionModal(true);
			return;
		}

		stopCorrecting();
	};

	// Discards the changes; when navigation triggered the modal, continue there.
	const discardCorrection = () => {
		const location = pendingLocation.current;
		pendingLocation.current = undefined;
		stopCorrecting();

		if (location) {
			// The block is removed once `correctionDirty` flips; push after that.
			setTimeout(() => history.push(location), 0);
		}
	};

	const saveCorrection = async () => {
		if (!props.snapshot || savingCorrection) {
			return;
		}

		const before = new Map(props.grid.placements.map((item) => [item.user.id, item]));
		const after = new Map(placements.map((item) => [item.user.id, item]));
		const changed = placements.filter((item) => {
			const original = before.get(item.user.id);
			return (
				!original ||
				original.row !== item.row ||
				original.column !== item.column ||
				(original.motivation ?? '') !== (item.motivation ?? '') ||
				(original.retentionRisk ?? null) !== (item.retentionRisk ?? null)
			);
		});
		const removedUserIDs = [...before.keys()].filter((userID) => !after.has(userID));

		try {
			await correctSnapshot({
				variables: {
					input: {
						snapshotID: props.snapshot.id,
						placements: changed.map((item) => ({
							userID: item.user.id,
							row: item.row,
							column: item.column,
							position: placementPosition(item),
							motivation: item.motivation,
							score: item.score,
							retentionRisk: item.retentionRisk,
						})),
						removedUserIDs,
					},
				},
			});
		} catch {
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
			return;
		}

		setShowSaveCorrectionModal(false);
		setCorrecting(false);
		addToast(t('talentReviews.v2.snapshot.correcting.saveSuccess'), {
			appearance: 'success',
			autoDismiss: true,
		});
	};

	const [dragging, setDragging] = useState(false);
	// Where the dragged person would land among the others in the cell under them.
	const insertionTracking = useTalentReviewInsertionTracking();
	const [showClearModal, setShowClearModal] = useState(false);
	const [showSubmitModal, setShowSubmitModal] = useState(false);
	const [reshareRecipients, setReshareRecipients] = useState<
		{ id: string; firstName: string; lastName: string }[] | null
	>(null);
	const [loadRecipients, { loading: loadingRecipients }] =
		graphql.useTalentReviewReshareRecipientsLazyQuery({
			fetchPolicy: 'network-only',
		});

	const openSubmitModal = async () => {
		if (!cycleMoment || loadingRecipients) {
			return;
		}

		const result = await loadRecipients({
			variables: {
				id: cycleMoment.id,
				templateID: props.grid.template.id,
				localDate: props.localDate,
			},
		});

		if (result.error || !result.data) {
			addToast(t('talentReviews.v2.saveFailed'), { appearance: 'error' });
			return;
		}

		setReshareRecipients(result.data.momentInstance.talentReviewReshareRecipients ?? null);
		setShowSubmitModal(true);
	};
	const [showRevertModal, setShowRevertModal] = useState(false);
	const focusedUserIDFromURL = talentReviewUserIDFromSearch(location.search);
	const [selectedUserID, setSelectedUserID] = useState<string | undefined>(focusedUserIDFromURL);
	// Click isolates that person on the grid (others hidden). A drop only
	// opens their sidebar card and rings them: the rest of the grid stays.
	// A user_id in the URL is isolation, so day and version switches keep it.
	const [isolated, setIsolated] = useState(!!focusedUserIDFromURL);
	// Whose sidebar row is hovered: their grid avatar shows the selection ring.
	const [hoveredUserID, setHoveredUserID] = useState<string>();
	// People moved during this page session, latest move last. Drives the
	// sidebar dump and the card's undo. Previous-day cell changes stay out.
	const [movedUserIDs, setMovedUserIDs] = useState<string[]>([]);
	// Bumped after a move or selection so the card can focus its textarea
	// when motivation is empty (not when a stored review is opened).
	const [focusKey, setFocusKey] = useState(0);
	const clearGridTimeout = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(clearGridTimeout.current), []);

	const replaceFocusedUserID = useCallback(
		(userID?: string) => {
			if (correctionDirty || talentReviewUserIDFromSearch(location.search) === userID) {
				return;
			}

			history.replace({
				pathname: location.pathname,
				search: talentReviewSearchWithUserID(location.search, userID),
			});
		},
		[correctionDirty, history, location.pathname, location.search],
	);

	const clearPersonFocus = useCallback(() => {
		setSelectedUserID(undefined);
		setIsolated(false);
		replaceFocusedUserID(undefined);
	}, [replaceFocusedUserID]);

	// Person focus is not reset on a URL day change: date arrows and snapshot
	// versions should keep you on the same teammate. movedUserIDs wait for the
	// new grid so the outgoing day stays intact while previousData is still
	// on screen.
	const [stateDate, setStateDate] = useState(props.grid.localDate);
	if (stateDate !== props.grid.localDate) {
		setStateDate(props.grid.localDate);
		setMovedUserIDs([]);
	}

	// The unplaced pool is only shown on today's live grid: earlier days and
	// snapshots show the grid as it was. Once everyone is placed it goes away;
	// taking someone off the grid then happens through undo or clearing the
	// grid (a droppable added mid-drag is not picked up by dnd anyway).
	const poolShown = !props.snapshot && props.localDate === today && unplacedUsers.length > 0;

	// Who is shown (cells, and the pool when visible) and who is new since the
	// last commit, for the avatar enter/exit animations. On the first render
	// nothing was committed yet, so everyone enters and grows in. Someone
	// unplaced on a day without a pool is not on the page, so their avatar
	// shrinks away.
	const presentUserIDs = useMemo(
		() =>
			new Set([
				...placements.map(({ user }) => user.id),
				...(poolShown ? unplacedUsers.map(({ id }) => id) : []),
			]),
		[placements, poolShown, unplacedUsers],
	);
	const committedUserIDs = useRef<Set<string>>();
	const presence = useMemo<TalentReviewPresence>(() => {
		const committed = committedUserIDs.current;
		const entering = new Set([...presentUserIDs].filter((id) => !committed?.has(id)));
		return { present: presentUserIDs, entering };
	}, [presentUserIDs]);
	useEffect(() => {
		committedUserIDs.current = presentUserIDs;
	}, [presentUserIDs]);
	// Pool order, so a drop into the pool lands where the person will mount.
	const memberOrder = useMemo(() => users.map(({ id }) => id), [users]);
	const [placementHelpOpen, setPlacementHelpOpen] = useState(false);
	const [placementHelp, setPlacementHelp] = useState<{
		from?: { label: string; color: number };
		row?: number;
		column?: number;
	}>();
	const selectedPlacement = placements.find((placement) => placement.user.id === selectedUserID);
	// A placed person who is no longer a group member only exists on the placement.
	const selectedUser =
		users.find((user) => user.id === selectedUserID) ?? (selectedPlacement?.user as graphql.User);

	// Drop the selection once the person left the grid and the pool (deleted,
	// cleared, or undone without a previous cell). Switching days or versions
	// keeps the focus when they are still on that grid.
	useEffect(() => {
		if (!selectedUserID) {
			return;
		}

		const placed = placements.some(({ user }) => user.id === selectedUserID);
		const unplaced = unplacedUsers.some((user) => user.id === selectedUserID);
		if (!placed && !unplaced) {
			clearPersonFocus();
		}
	}, [clearPersonFocus, placements, selectedUserID, unplacedUsers]);

	/**
	 * Moves a person; the drop saves right away.
	 * @param target where to put whom
	 * @param target.userID the person to place
	 * @param target.row target cell row
	 * @param target.column target cell column
	 * @param target.position place among the others in the cell; omitted = at the end
	 * @param target.renumbered new positions for others in the cell, when there was no room between two of them
	 */
	const move = (target: {
		userID: string;
		row: number;
		column: number;
		position?: number;
		renumbered?: ReadonlyMap<string, number>;
	}) => {
		movePlacement(target);
		setMovedUserIDs((current) =>
			current.filter((userID) => userID !== target.userID).concat(target.userID),
		);
	};

	/**
	 * Click a person: isolate them on the grid. Click the isolated person
	 * again, or pass nobody: clear. A drop uses `moveAndSelect` instead, so
	 * the grid stays visible.
	 * @param userID the person to isolate, or nobody
	 */
	const selectUser = (userID?: string) => {
		if (!userID || (userID === selectedUserID && isolated)) {
			clearPersonFocus();
			return;
		}

		setSelectedUserID(userID);
		setIsolated(true);
		setFocusKey((key) => key + 1);
		replaceFocusedUserID(userID);
	};

	/**
	 * Moves a person and opens their sidebar card for motivation. Isolation is
	 * left as it is: an isolated person stays alone on the grid after the drop,
	 * otherwise everyone stays visible and the moved person is highlighted.
	 * @param target where to put whom
	 * @param target.userID the person to place
	 * @param target.row target cell row
	 * @param target.column target cell column
	 * @param target.position place among the others in the cell; omitted = at the end
	 * @param target.renumbered new positions for others in the cell, when there was no room between two of them
	 */
	const moveAndSelect = (target: {
		userID: string;
		row: number;
		column: number;
		position?: number;
		renumbered?: ReadonlyMap<string, number>;
	}) => {
		move(target);
		setSelectedUserID(target.userID);
		setFocusKey((key) => key + 1);
	};

	// Back to the definitive placement of the last earlier day; a person first
	// placed today leaves the grid again.
	const undoMove = (userID: string) => {
		const placement = placements.find(({ user }) => user.id === userID);
		if (placement?.previous) {
			movePlacement({
				userID,
				row: placement.previous.row,
				column: placement.previous.column,
				// Back to the old place in the cell too, when that write had one.
				position: placement.previous.position ?? undefined,
				motivation: placement.previous.motivation,
			});
		} else {
			removePlacement(userID);
		}
		setMovedUserIDs((current) => current.filter((id) => id !== userID));
	};
	const modalOpen =
		showClearModal ||
		showSubmitModal ||
		showRevertModal ||
		showSaveCorrectionModal ||
		showCancelCorrectionModal ||
		placementHelpOpen;
	const clearPersonFocusRef = useRef(clearPersonFocus);
	clearPersonFocusRef.current = clearPersonFocus;

	// Escape anywhere on the page leaves the isolated view. Escape inside the
	// sidebar, a modal, a menu or a drag (react-beautiful-dnd cancels it) is
	// handled there.
	useEffect(() => {
		if (!isolated || !selectedUserID || dragging || modalOpen) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || event.defaultPrevented) {
				return;
			}

			const target = event.target instanceof Element ? event.target : null;
			if (
				target?.closest(
					'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]',
				)
			) {
				return;
			}

			event.preventDefault();
			clearPersonFocusRef.current();
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isolated, selectedUserID, dragging, modalOpen]);

	const cells = props.grid.template.cells;
	const columnCount = cells[0]?.length ?? 0;
	const positionLabel = (row: number, column: number) =>
		placementHelpPositionLabel({
			cellLabel: localize(cells[row]?.[column]?.label)?.text,
			xTick: localize(props.grid.template.xAxis.ticks[column])?.text,
			yTick: localize(props.grid.template.yAxis.ticks[row])?.text,
		});
	const current = selectedPlacement
		? {
				label: positionLabel(selectedPlacement.row, selectedPlacement.column),
				color: cellColorValue(
					selectedPlacement.row,
					selectedPlacement.column,
					cells.length,
					columnCount,
					cells[selectedPlacement.row]?.[selectedPlacement.column]?.color,
				),
			}
		: undefined;
	const placementHelpQuestions = useMemo(
		() =>
			questionsOnTemplate<TemplatePlacementHelpQuestion>(props.grid.template).map((question) => {
				const axis = asPlacementHelpAxis(question.axis);
				const axisLabel =
					localize(axis === 'X' ? props.grid.template.xAxis.label : props.grid.template.yAxis.label)
						?.text ||
					t(
						axis === 'X'
							? 'talentReviews.v2.template.edit.placementHelp.axisX'
							: 'talentReviews.v2.template.edit.placementHelp.axisY',
					);

				return {
					id: question.id,
					axis,
					axisLabel,
					question: withPlacementHelpName(
						localize(question.question)?.text ?? '',
						selectedUser?.firstName ?? '',
					),
					answers: question.answers.map((answer) => ({
						id: answer.id,
						label: localize(answer.label)?.text ?? '',
						tickIndex: answer.tickIndex,
					})),
				};
			}),
		[localize, props.grid.template, selectedUser?.firstName, t],
	);

	const overdue = daysLeft !== undefined && daysLeft < 0;

	const handleSubmitTalentReview = async () => {
		if (!cycleMoment || submitting) {
			return;
		}

		const fallbackTitle = t('talentReviews.v2.snapshot.create.namePlaceholder', {
			date: moment().format('L'),
		});
		const title = localize(cycleMoment.momentTemplate.title).text || fallbackTitle;

		setOptimisticMomentID(cycleMoment.id);

		try {
			await createSnapshot({
				variables: {
					input: {
						groupID: props.grid.group.id,
						templateID: props.grid.template.id,
						title,
						localDate: props.localDate,
						momentID: cycleMoment.id,
					},
				},
			});
		} catch {
			setOptimisticMomentID(undefined);
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
			return;
		}

		setShowSubmitModal(false);
		addToast(t('talentReviews.v2.cycle.submitSuccess'), {
			appearance: 'success',
			autoDismiss: true,
		});
	};

	const handleRevertTalentReview = async () => {
		if (!cycleMoment || reverting) {
			return;
		}

		setOptimisticMomentID(undefined);

		try {
			await revertSubmission({ variables: { input: { momentID: cycleMoment.id } } });
		} catch {
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
			return;
		}

		setShowRevertModal(false);
		addToast(t('talentReviews.v2.cycle.revertSuccess'), {
			appearance: 'success',
			autoDismiss: true,
		});
	};

	const past = isTalentReviewHistoryDay(props.localDate, today, !!props.snapshot);
	const cycleTitle = cycleMoment ? (localize(cycleMoment.momentTemplate.title).text ?? '') : '';
	const cycleDeadline = cycleMoment?.endDate
		? formatTalentReviewDeadline(cycleMoment.endDate)
		: undefined;
	const submission = cycleMoment?.talentReviewSnapshot;
	const optimisticSubmitted = optimisticMomentID === cycleMoment?.id;

	useEffect(() => {
		if (submitted && optimisticSubmitted) {
			setOptimisticMomentID(undefined);
		}
	}, [optimisticSubmitted, submitted]);

	const showSubmitted = submitted || optimisticSubmitted;
	const submissionDate = submission
		? moment(submission.createdAt).format('LL')
		: optimisticSubmitted
			? moment().format('LL')
			: undefined;
	const submissionName = submission
		? `${submission.createdBy.firstName} ${submission.createdBy.lastName}`
		: optimisticSubmitted
			? `${authenticatedUser.firstName} ${authenticatedUser.lastName}`
			: undefined;

	const content = (
		<>
			{props.header ?? (
				<Box mb={4}>
					<TalentReviewPageHeader
						backLink={path()}
						title={
							selectedUser
								? `${selectedUser.firstName} ${selectedUser.lastName}`
								: props.grid.group.name
						}
						user={selectedUser}
						groupName={props.grid.group.name}
						templateID={props.templateID}
						groupID={props.groupID}
						localDate={props.localDate}
						placement={
							selectedPlacement
								? { row: selectedPlacement.row, column: selectedPlacement.column }
								: undefined
						}
						past={past}
						onClearFocus={() => selectUser(undefined)}
						onStartPlacementHelp={
							placementHelpQuestions.length
								? () => {
										setPlacementHelp({
											from: current,
											row: selectedPlacement?.row,
											column: selectedPlacement?.column,
										});
										setPlacementHelpOpen(true);
									}
								: undefined
						}
					/>
				</Box>
			)}
			{cycleMoment && showSubmitted && !props.snapshot && (
				<Callout
					color="green"
					mt={3}
					mb={3}
					title={t('talentReviews.v2.cycle.submittedTitle', { title: cycleTitle })}
					description={t(
						cycleDeadline
							? 'talentReviews.v2.cycle.submittedDescription'
							: 'talentReviews.v2.cycle.submittedDescriptionNoDeadline',
						{ date: submissionDate, name: submissionName, deadline: cycleDeadline },
					)}
					action={
						cycleMoment.talentReviewViewerCanRevert && (
							<Button
								type="secondary"
								role="button"
								aria-label={t('talentReviews.v2.cycle.revert')}
								icon={<UndoIcon />}
								title={t('talentReviews.v2.cycle.revert')}
								loading={reverting}
								disabled={reverting}
								onClick={() => setShowRevertModal(true)}
							/>
						)
					}
				/>
			)}
			{cycleMoment && !showSubmitted && !props.snapshot && (
				<Callout
					color="yellow"
					mt={3}
					mb={3}
					title={t('talentReviews.v2.cycle.title', {
						title: localize(cycleMoment.momentTemplate.title).text,
					})}
					description={
						<>
							{t('talentReviews.v2.cycle.instruction')}
							{cycleMoment.endDate && (
								<>
									<br />
									{t(
										overdue
											? 'talentReviews.v2.cycle.deadlineOverdue'
											: 'talentReviews.v2.cycle.deadline',
										{
											date: formatTalentReviewDeadline(cycleMoment.endDate),
											count: overdue ? Math.abs(daysLeft) : daysLeft,
										},
									)}
								</>
							)}
						</>
					}
					action={
						<Button
							type="primary"
							role="button"
							aria-label={t('talentReviews.v2.cycle.submit')}
							icon={<CircleCheckmarkIcon />}
							title={t('talentReviews.v2.cycle.submit')}
							loading={submitting || loadingRecipients}
							disabled={
								stale || props.localDate !== today || !props.grid.viewerCanUpdate || submitting
							}
							onClick={openSubmitModal}
						/>
					}
				/>
			)}
			{showRevertModal && cycleMoment && (
				<RevertTalentReviewModal
					title={cycleTitle}
					deadline={cycleDeadline}
					loading={reverting}
					onClose={() => setShowRevertModal(false)}
					onConfirm={handleRevertTalentReview}
				/>
			)}
			{showSubmitModal && cycleMoment && (
				<SubmitTalentReviewModal
					title={localize(cycleMoment.momentTemplate.title).text ?? ''}
					visibility={props.grid.template.visibility}
					groupName={props.grid.group.name}
					recipients={reshareRecipients}
					loading={submitting}
					onClose={() => setShowSubmitModal(false)}
					onConfirm={handleSubmitTalentReview}
				/>
			)}

			<Flex my={4} alignItems="center" justifyContent="space-between">
				<Flex alignItems="center" style={{ gap: tokens.gap.group }} minWidth={0}>
					<TalentReviewSectionTitle>
						{localize(props.grid.template.title).text}
					</TalentReviewSectionTitle>
					{(props.snapshot || props.localDate === today) && (
						<TalentReviewCurrentSelect
							groupID={props.grid.group.id}
							templateID={props.grid.template.id}
							localDate={props.localDate}
							selectedSnapshotID={props.snapshot?.id}
						/>
					)}
				</Flex>
				<Flex alignItems="center" style={{ gap: tokens.gap.inline }} flexShrink={0}>
					{!props.snapshot && (
						<TalentReviewDateNavigation
							groupID={props.grid.group.id}
							templateID={props.grid.template.id}
							localDate={props.localDate}
							activity={props.grid.activity}
						/>
					)}
					<TalentReviewGridActionsMenu
						clearDisabled={readonly || placements.length === 0}
						onClearGrid={() => setShowClearModal(true)}
						snapshot={props.snapshot}
						groupID={props.grid.group.id}
						templateID={props.grid.template.id}
						templateName={localize(props.grid.template.title).text ?? ''}
						localDate={props.localDate}
					/>
				</Flex>
			</Flex>

			{props.snapshot && correcting && (
				<Callout
					color="yellow"
					mb={4}
					icon={<PencilIcon size={20} />}
					title={t('talentReviews.v2.snapshot.correcting.title')}
					description={t('talentReviews.v2.snapshot.correcting.description')}
					action={
						<Flex style={{ gap: tokens.gap.element }}>
							<Button
								type="primary"
								icon={<CheckmarkIcon />}
								title={t('talentReviews.v2.snapshot.correcting.save')}
								disabled={!correctionDirty || savingCorrection}
								loading={savingCorrection}
								onClick={() => setShowSaveCorrectionModal(true)}
							/>
							<Button
								type="secondary"
								title={t('talentReviews.v2.snapshot.correcting.cancel')}
								disabled={savingCorrection}
								onClick={cancelCorrection}
							/>
						</Flex>
					}
				/>
			)}
			{props.snapshot && !correcting && (
				<Callout
					color="blue"
					mb={4}
					title={t('talentReviews.v2.snapshot.viewing.title')}
					description={t(
						props.snapshot.viewerCanCorrect
							? 'talentReviews.v2.snapshot.viewing.descriptionCorrectable'
							: 'talentReviews.v2.snapshot.viewing.description',
						{
							date: props.snapshot.submittedAt,
							name: props.snapshot.submittedBy,
						},
					)}
					action={
						<Flex style={{ gap: tokens.gap.element }}>
							<Button
								type="primary"
								icon={<ArrowLeftIcon />}
								title={t('talentReviews.v2.snapshot.viewing.backToCurrent')}
								link={path({
									groupID: props.grid.group.id,
									templateID: props.grid.template.id,
									localDate: today,
								})}
							/>
							{props.snapshot.viewerCanCorrect && (
								<Button
									type="secondary"
									icon={<PencilIcon />}
									title={t('talentReviews.v2.snapshot.correct')}
									onClick={() => setCorrecting(true)}
								/>
							)}
						</Flex>
					}
				/>
			)}
			{showSaveCorrectionModal && props.snapshot && (
				<SaveTalentReviewCorrectionModal
					date={props.snapshot.localDate}
					loading={savingCorrection}
					onClose={() => setShowSaveCorrectionModal(false)}
					onConfirm={saveCorrection}
				/>
			)}
			{showCancelCorrectionModal && (
				<CancelTalentReviewCorrectionModal
					changes={correctionChanges}
					onContinue={() => {
						pendingLocation.current = undefined;
						setShowCancelCorrectionModal(false);
					}}
					onDiscard={discardCorrection}
				/>
			)}

			{/* Unplaced people only matter on the live grid of today; past days
			    and snapshots show the grid as it was. */}
			<AnimatePresence initial={false}>
				{poolShown && (
					<TalentReviewUnplaced
						key="unplaced"
						users={unplacedUsers}
						readonly={readonly}
						selectedUserID={selectedUserID}
						dragging={dragging}
						onSelectUser={selectUser}
					/>
				)}
			</AnimatePresence>

			<TalentReviewGridContainer
				grid={props.grid}
				localDate={props.localDate}
				snapshot={props.snapshot}
				placements={placements}
				dragging={dragging}
				readonly={readonly}
				focusUserID={isolated ? selectedUserID : undefined}
				highlightUserID={isolated ? undefined : (selectedUserID ?? hoveredUserID)}
				onSelectUser={selectUser}
			/>

			<TalentReviewActivity
				template={props.grid.template}
				groupName={props.grid.group.name}
				activity={activity}
				userID={selectedUserID}
			/>
		</>
	);

	return (
		<TalentReviewPresenceContext.Provider value={presence}>
			<TalentReviewMemberOrderContext.Provider value={memberOrder}>
				<DragDropContext
					onDragStart={(start) => {
						setDragging(true);
						// An isolated person stays isolated while dragged: the others stay
						// hidden (they still open a slot, see TalentReviewCell).
						insertionTracking.onDragStart(start);
					}}
					onDragUpdate={insertionTracking.onDragUpdate}
					onDragEnd={(result) => {
						// In the same render as the placement change below, so the avatar
						// mounting where the drop landed always mounts with its layoutId
						// (framer only registers shared nodes on mount). It does not glide
						// from the one leaving its old spot: that one is `display: none` by
						// then (see `TalentReviewPerson`). Delaying this a frame raced React's
						// render, so the mount was sometimes shared and glided a second time.
						setDragging(false);
						const insertion = insertionTracking.getInsertion();
						insertionTracking.onDragEnd();

						if (stale || !result.destination) {
							return;
						}

						if (result.destination.droppableId === 'team') {
							// Dropping a user who was never placed (pool → pool) needs no
							// remove mutation.
							if (placements.some(({ user }) => user.id === result.draggableId)) {
								removePlacement(result.draggableId);
							}
							if (selectedUserID === result.draggableId) {
								clearPersonFocus();
							}
							return;
						}

						if (!result.destination.droppableId.startsWith('cell-')) {
							return;
						}

						const [row, column] = result.destination.droppableId
							.split('-')
							.slice(1)
							.map((x) => parseInt(x));

						// Where among the cell's others the person goes: the tracked insertion
						// point (see talentReviewInsertion), or the end when none was tracked.
						const cell = cellPlacements(placements, row, column);
						const others = cell.filter(({ user }) => user.id !== result.draggableId);
						const index =
							insertion?.target?.droppableId === result.destination.droppableId
								? Math.min(insertion.target.index, others.length)
								: others.length;
						const current = placements.find(({ user }) => user.id === result.draggableId);
						// Dropped back into the same cell: dropped where they were, nothing
						// changed, so no write (that would bump createdAt). Dropped among the
						// others, only their place changes: written, but not a move (no
						// sidebar card, no activity line; the server hides same-cell writes).
						if (current && current.row === row && current.column === column) {
							if (cell.indexOf(current) === index) {
								return;
							}
							const { position, renumbered } = insertPosition(others, index);
							updatePlacements([
								...others
									.filter(({ user }) => renumbered?.has(user.id))
									.map((other) => ({ ...other, position: renumbered!.get(other.user.id) })),
								{ ...current, position },
							]);
							return;
						}

						const target = {
							userID: result.draggableId,
							row,
							column,
							...insertPosition(others, index),
						};
						// A drop onto another cell opens the sidebar card so motivation
						// can be left right away. Same-cell reorder above is not a move.
						moveAndSelect(target);
					}}
				>
					<TalentReviewSplitLayout
						ref={splitRef}
						sidebar={
							<TalentReviewSidebar
								localDate={props.grid.localDate}
								template={props.grid.template}
								placements={placements}
								selectedUserID={selectedUserID}
								onHoverUser={setHoveredUserID}
								movedUserIDs={movedUserIDs}
								readonly={readonly}
								focusKey={focusKey}
								onSelectUser={selectUser}
								onRequestFocus={() => setFocusKey((key) => key + 1)}
								onUpdateMotivation={updateMotivation}
								onFlushMotivation={flushMotivation}
								onUpdatePlacement={updatePlacement}
								onMovePlacement={(userID, row, column) => moveAndSelect({ userID, row, column })}
								onUndoMove={undoMove}
							/>
						}
					>
						<Container py={4}>
							{/* One layout group so avatars animate between the unplaced list and the grid. */}
							<TalentReviewInsertionContext.Provider value={insertionTracking.insertion}>
								<LayoutGroup>{content}</LayoutGroup>
							</TalentReviewInsertionContext.Provider>
						</Container>
					</TalentReviewSplitLayout>
					{showClearModal && (
						<ClearTalentReviewGridModal
							onClose={() => setShowClearModal(false)}
							onConfirm={() => {
								// Close the modal first and clear shortly after, so the avatars are seen
								// animating from the grid back into the unplaced list.
								setShowClearModal(false);
								clearTimeout(clearGridTimeout.current);
								clearGridTimeout.current = setTimeout(clearGrid, 200);
							}}
						/>
					)}
					{placementHelpOpen && selectedUser && placementHelp && (
						<TalentReviewPlacementHelpModal
							key={selectedUser.id}
							user={selectedUser}
							questions={placementHelpQuestions}
							current={placementHelp.from}
							currentCell={
								placementHelp.row !== undefined && placementHelp.column !== undefined
									? { row: placementHelp.row, column: placementHelp.column }
									: undefined
							}
							rowCount={cells.length}
							columnCount={columnCount}
							cellPosition={(row, column) => ({
								label: positionLabel(row, column),
								color: cellColorValue(
									row,
									column,
									cells.length,
									columnCount,
									cells[row]?.[column]?.color,
								),
							})}
							onMove={
								readonly
									? undefined
									: (row, column) => {
											const already =
												!!selectedPlacement &&
												selectedPlacement.row === row &&
												selectedPlacement.column === column;
											if (!already) {
												moveAndSelect({
													userID: selectedUser.id,
													row,
													column,
												});
											}
											setPlacementHelpOpen(false);
											setPlacementHelp(undefined);
										}
							}
							onAnswered={
								readonly
									? undefined
									: (answers) => void answerPlacementHelp(selectedUser.id, answers)
							}
							onClose={() => {
								setPlacementHelpOpen(false);
								setPlacementHelp(undefined);
							}}
						/>
					)}
				</DragDropContext>
			</TalentReviewMemberOrderContext.Provider>
		</TalentReviewPresenceContext.Provider>
	);
}
